import { useCallback, useEffect } from 'react';
import type { FormInstance } from 'antd/es/form';

import type { PaymentFormValues, PaymentPlanOption } from '../types';

import { initializeSplitAmountsEqual, initializeSplitDownEqual } from '../utils/split-amount-sync';
import { minDownPaymentAmount, parseMoneyInput, roundCurrency2 } from '../utils/payment-math';

import type { PaymentFormRefs } from './usePaymentFormRefs';
import type { PaymentFormWatchedValues } from './usePaymentFormWatchedValues';

interface UsePaymentFormSyncEffectsParams {
  form: FormInstance<PaymentFormValues>;
  certificateTotal: number;
  certificateTemplateTotalPrice: number | undefined;
  watched: PaymentFormWatchedValues;
  refs: Pick<
    PaymentFormRefs,
    'prevDownForSplitRef' | 'prevSplitPayInFullRef' | 'prevSplitInstalmentDownRef'
  >;
}

export function usePaymentFormSyncEffects({
  form,
  certificateTotal,
  certificateTemplateTotalPrice,
  watched,
  refs,
}: UsePaymentFormSyncEffectsParams): { handlePlanChange: (plan: PaymentPlanOption) => void } {
  const {
    paymentPlan,
    splitPayInFull,
    splitInstalmentDown,
    downPaymentAmount,
    billingAddressSameAsFirst,
    billing1AddressLine,
    billing1City,
    billing1Country,
    billing1PostalCode,
  } = watched;

  const { prevDownForSplitRef, prevSplitPayInFullRef, prevSplitInstalmentDownRef } = refs;

  const handlePlanChange = useCallback(
    (plan: PaymentPlanOption) => {
      const total = certificateTemplateTotalPrice ?? 0;
      const min = minDownPaymentAmount(total);
      const clearSecondCard = {
        cardNumber2: '',
        cardExpiry2: '',
        cardCvc2: '',
        nameOnCard2: '',
      };
      if (plan === 'instalments') {
        form.setFieldsValue({
          paymentPlan: 'instalments',
          splitPayInFull: false,
          splitAmount1: '',
          splitAmount2: '',
          billingAddressSameAsFirst: false,
          addressLine2: '',
          city2: '',
          country2: 'US',
          postalCode2: '',
          downPaymentAmount: roundCurrency2(min).toFixed(2),
          instalmentMonths: 4,
          ...clearSecondCard,
        });
      } else {
        form.setFieldsValue({
          paymentPlan: 'full',
          splitInstalmentDown: false,
          splitDownAmount1: '',
          splitDownAmount2: '',
          ...clearSecondCard,
        });
      }
      prevDownForSplitRef.current = null;
      prevSplitPayInFullRef.current = undefined;
      prevSplitInstalmentDownRef.current = undefined;
    },
    [
      certificateTemplateTotalPrice,
      form,
      prevDownForSplitRef,
      prevSplitInstalmentDownRef,
      prevSplitPayInFullRef,
    ],
  );

  useEffect(() => {
    if (paymentPlan !== 'full') {
      return;
    }
    if (prevSplitPayInFullRef.current === undefined) {
      prevSplitPayInFullRef.current = splitPayInFull ?? false;
      return;
    }
    if (prevSplitPayInFullRef.current === splitPayInFull) {
      return;
    }
    prevSplitPayInFullRef.current = splitPayInFull ?? false;
    if (splitPayInFull) {
      form.setFieldsValue({
        ...initializeSplitAmountsEqual(certificateTotal),
        billingAddressSameAsFirst: false,
        addressLine2: '',
        city2: '',
        country2: 'US',
        postalCode2: '',
        nameOnCard2: '',
        cardNumber2: '',
        cardExpiry2: '',
        cardCvc2: '',
      });
    } else {
      form.setFieldsValue({
        splitAmount1: '',
        splitAmount2: '',
        billingAddressSameAsFirst: false,
        addressLine2: '',
        city2: '',
        country2: 'US',
        postalCode2: '',
        nameOnCard2: '',
        cardNumber2: '',
        cardExpiry2: '',
        cardCvc2: '',
      });
    }
  }, [certificateTotal, form, paymentPlan, prevSplitPayInFullRef, splitPayInFull]);

  useEffect(() => {
    const payFullSplitSame = paymentPlan === 'full' && splitPayInFull && billingAddressSameAsFirst;
    const instalmentSplitSame =
      paymentPlan === 'instalments' && splitInstalmentDown && billingAddressSameAsFirst;
    if (!payFullSplitSame && !instalmentSplitSame) {
      return;
    }
    form.setFieldsValue({
      addressLine2: billing1AddressLine ?? '',
      city2: billing1City ?? '',
      country2: billing1Country ?? 'US',
      postalCode2: billing1PostalCode ?? '',
    });
  }, [
    billing1AddressLine,
    billing1City,
    billing1Country,
    billing1PostalCode,
    billingAddressSameAsFirst,
    form,
    paymentPlan,
    splitInstalmentDown,
    splitPayInFull,
  ]);

  const minDown = minDownPaymentAmount(certificateTotal);

  useEffect(() => {
    if (paymentPlan !== 'instalments') {
      return;
    }
    if (prevSplitInstalmentDownRef.current === undefined) {
      prevSplitInstalmentDownRef.current = splitInstalmentDown ?? false;
      return;
    }
    if (prevSplitInstalmentDownRef.current === splitInstalmentDown) {
      return;
    }
    prevSplitInstalmentDownRef.current = splitInstalmentDown ?? false;
    const min = minDownPaymentAmount(certificateTotal);
    const rawParsed = parseMoneyInput(String(form.getFieldValue('downPaymentAmount') ?? ''));
    const rawDown = rawParsed !== null ? rawParsed : min;
    const currentDown = roundCurrency2(Math.min(Math.max(rawDown, min), certificateTotal));
    if (splitInstalmentDown) {
      form.setFieldsValue({
        ...initializeSplitDownEqual(currentDown),
        primaryInstalmentCard: 'card1',
        nameOnCard2: '',
        cardNumber2: '',
        cardExpiry2: '',
        cardCvc2: '',
      });
      prevDownForSplitRef.current = currentDown;
    } else {
      form.setFieldsValue({
        splitDownAmount1: '',
        splitDownAmount2: '',
        nameOnCard2: '',
        cardNumber2: '',
        cardExpiry2: '',
        cardCvc2: '',
      });
      prevDownForSplitRef.current = null;
    }
  }, [
    certificateTotal,
    form,
    paymentPlan,
    prevDownForSplitRef,
    prevSplitInstalmentDownRef,
    splitInstalmentDown,
  ]);

  useEffect(() => {
    if (paymentPlan !== 'instalments' || !splitInstalmentDown) {
      prevDownForSplitRef.current = null;
      return;
    }
    const parsedDown = parseMoneyInput(String(downPaymentAmount ?? ''));
    const down = roundCurrency2(parsedDown !== null ? parsedDown : minDown);
    if (prevDownForSplitRef.current === down) {
      return;
    }
    prevDownForSplitRef.current = down;
    form.setFieldsValue(initializeSplitDownEqual(down));
  }, [downPaymentAmount, form, minDown, paymentPlan, prevDownForSplitRef, splitInstalmentDown]);

  return { handlePlanChange };
}
